import { useState, useRef, useEffect, useCallback } from 'react';
import {
  createManualServiceOrder,
  saveManualServiceOrder,
  submitManualServiceOrder,
  getPrefilledCart,
  updateOrderNote,
  cancelOrder,
  markAllItemsOutOfStock
} from '../services/subscriberWalletService';
import { mapCartItemFromAPI } from '../utils/medicineHelpers';

export const useMedicineOrder = (currentRole, id, isModal, walletTransactionId, medicineProviders) => {
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [orderDeliveryCharge, setOrderDeliveryCharge] = useState(null);
  const [orderDeliveryChargeError, setOrderDeliveryChargeError] = useState(null);
  const [handlingPackagingCharge, setHandlingPackagingCharge] = useState(null);
  const [handlingPackagingChargeError, setHandlingPackagingChargeError] = useState(null);
  const [platformCharge, setPlatformCharge] = useState(null);
  const [platformChargeError, setPlatformChargeError] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [vendorId, setVendorId] = useState(null);
  const [serviceProvider, setServiceProvider] = useState('');
  // Two ids, deliberately separate: what the ORDER holds, and what ops have picked but not yet
  // saved. The gap between them is what the selector reports as an unsaved pick.
  const [orderAddressId, setOrderAddressId] = useState(null);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);
  // The server's OWN freeze flag, not a status list re-derived on the client. `save_order`,
  // `update`, `out_of_stock` and the item router all gate on exactly this one field, and a
  // status list drifts from it: `cancel` never sets it, `ordered` does. Mirroring the flag
  // is how the read-only gate stays honest.
  const [isPaymentLocked, setIsPaymentLocked] = useState(false);
  // Paid per the order's transaction. The order row stays `initiated` after an in-app, link or
  // credit-line payment and `is_payment_locked` stays false, so neither says "paid".
  const [isPaid, setIsPaid] = useState(false);

  const [savingOrder, setSavingOrder] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [markingAllOutOfStock, setMarkingAllOutOfStock] = useState(false);
  const [updatingNote, setUpdatingNote] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  
  const orderLoadedRef = useRef(false);
  const prefilledCartLoadedRef = useRef(false);

  // Reset prefilled cart ref when modal closes
  useEffect(() => {
    if (!isModal) {
      prefilledCartLoadedRef.current = false;
    }
  }, [isModal]);

  // Create order
  // Pass vendor_id from provider.id (which is the correct vendor_id from provider list API)
  // This ensures order is created with correct vendor_id that matches catalog items
  const createOrder = useCallback(async (vendorId, providerName) => {
    if (!id || !currentRole) return null;
    if (isModal) return orderId || null;

    try {
      const orderData = {
        application_id: id,
        service_type: 'medicine',
        vendor_id: vendorId, // Use vendor_id from provider.id (correct vendor_id)
        service_provider: providerName
      };
      
      const response = await createManualServiceOrder(currentRole, orderData);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        const returnedOrderId = response.msg?.id || response.msg?.order_id;
        setOrderId(returnedOrderId);
        setOrderStatus(response.msg?.status);
        
        // Use vendor_id from response if available, otherwise use the one we passed
        if (response.msg?.vendor_id) {
          setVendorId(response.msg.vendor_id);
        } else if (vendorId) {
          setVendorId(vendorId);
        }
        
        // Store service_provider name
        if (providerName) {
          setServiceProvider(providerName);
        }

        // The backend stamps a delivery address at creation, so a fresh order already has one
        // to preselect — ops only have to touch the selector when they want a different one.
        const createdAddressId = response.msg?.address_id || null;
        setOrderAddressId(createdAddressId);
        // Seed only when ops have not already chosen: the stamped address is a default, and
        // picking a provider must not silently undo a pick made before the order existed.
        setSelectedAddressId((previous) => previous || createdAddressId);

        return returnedOrderId;
      } else {
        throw new Error(response?.msg?.error || response?.body?.error || 'Failed to create order');
      }
    } catch (error) {
      throw error;
    }
  }, [id, currentRole, isModal, orderId]);

  // Load prefilled cart (for modal mode)
  const loadPrefilledCart = useCallback(async (setCart, setCartCashback, setSelectedVendorId, setProviderSearchText, setMedicineServiceProvider, showNotification) => {
    if (!isModal || (!id && !walletTransactionId) || !currentRole || prefilledCartLoadedRef.current) return;
    if (!medicineProviders || medicineProviders.length === 0) return;
    
    prefilledCartLoadedRef.current = true;
    try {
      const response = await getPrefilledCart(currentRole, walletTransactionId ? null : id, walletTransactionId);
      
      if (response && response.metadata && response.metadata.api_status === true && response.msg) {
        const cartData = response.msg;
        
        if (cartData.has_cart) {
          setOrderId(cartData.id);
          setOrderStatus(cartData.status);
          
          // CRITICAL: Store vendor_id from order detail (this is the correct vendor_id that matches catalog items)
          if (cartData.vendor_id) {
            setVendorId(cartData.vendor_id);
            // Use vendor_id from order for selectedVendorId (not provider.id from provider list)
            setSelectedVendorId(cartData.vendor_id);
          }
          
          if (cartData.service_provider || cartData.vendor_name) {
            const providerName = cartData.service_provider || cartData.vendor_name;
            setServiceProvider(providerName);
            setProviderSearchText(providerName);
            
            // Find provider for UI display only (don't use provider.id as vendor_id)
            const provider = medicineProviders.find(p => p.name === providerName);
            if (provider) {
              setMedicineServiceProvider(provider.id);
            } else if (cartData.vendor_id) {
              // Use vendor_id from order if provider not found in list
              setMedicineServiceProvider(cartData.vendor_id);
            } else {
              setMedicineServiceProvider('');
            }
          }
          
          if (cartData.delivery_charges !== undefined && cartData.delivery_charges !== null) {
            setOrderDeliveryCharge(cartData.delivery_charges);
          } else {
            setOrderDeliveryCharge(null);
          }
          
          if (cartData.handling_packaging_charge !== undefined && cartData.handling_packaging_charge !== null) {
            setHandlingPackagingCharge(cartData.handling_packaging_charge);
          } else {
            setHandlingPackagingCharge(null);
          }
          
          if (cartData.platform_charge !== undefined && cartData.platform_charge !== null) {
            setPlatformCharge(cartData.platform_charge);
          } else {
            setPlatformCharge(null);
          }
          
          if (cartData.cashback !== undefined && cartData.cashback !== null) {
            setCartCashback(cartData.cashback);
          } else {
            setCartCashback(0);
          }
          
          if (cartData.order_note !== undefined && cartData.order_note !== null && cartData.order_note !== '') {
            setNoteText(cartData.order_note);
            setSavedNote(cartData.order_note);
          } else {
            setNoteText('');
            setSavedNote('');
          }
          
          // The address this order actually carries. `delivery_address` may still be a
          // subscriber-level fallback for older orders, so only the order's own id seeds the
          // selection — the selector fills a null in from the subscriber's list.
          // Only the order's OWN address preselects. `delivery_address` may be a subscriber-level
          // fallback resolved for display, and preselecting a fallback would let a plain Save
          // write an address nobody chose — on an order that had none, that silently changes
          // where the medicine goes.
          const cartAddressId = cartData.address_id || null;
          setOrderAddressId(cartAddressId);
          setSelectedAddressId(cartAddressId);

          // contract: internalapi/views/user_type/shared/manual_service/list_detail/serializers.py:36
          // Absent on an older response reads as "not locked" — the server still refuses the
          // write (save.py's is_payment_locked guard), so the worst case is an error toast,
          // never a write that should not have happened.
          setIsPaymentLocked(Boolean(cartData.is_payment_locked));
          // contract: internalapi/views/user_type/shared/manual_service/list_detail/serializers.py (is_paid)
          setIsPaid(Boolean(cartData.is_paid));

          // Prescriptions the subscriber attached to this order (read-only) —
          // ops needs them before placing the order with the pharmacy.
          setPrescriptions(cartData.prescriptions || []);

          const itemsArray = cartData.items || cartData.cart_items || [];
          if (itemsArray && Array.isArray(itemsArray) && itemsArray.length > 0) {
            const cartItems = itemsArray.map(mapCartItemFromAPI);
            setCart(cartItems);
          } else {
            setCart([]);
          }
        }
      }
    } catch (error) {
      showNotification('Failed to load prefilled cart', 'error');
    }
  }, [isModal, id, walletTransactionId, currentRole, medicineProviders]);

  // Save order
  const saveOrder = useCallback(async (cart, batchUpdateCartItems, showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Order not created. Please select a service provider first.', 'error');
      return;
    }
    
    setSavingOrder(true);
    try {
      await batchUpdateCartItems();
      
      const response = await saveManualServiceOrder(currentRole, orderId, {
        delivery_charges: orderDeliveryCharge !== null && orderDeliveryCharge !== undefined ? orderDeliveryCharge : 0,
        handling_packaging_charge: handlingPackagingCharge !== null && handlingPackagingCharge !== undefined ? handlingPackagingCharge : 0,
        platform_charge: platformCharge !== null && platformCharge !== undefined ? platformCharge : 0,
        taxes: 0,
        partner_charges: 0,
        // Omitted when nothing is picked — the backend reads an absent key as "leave it alone".
        ...(selectedAddressId ? { address_id: selectedAddressId } : {})
      });
      
      if (response && response.metadata && response.metadata.api_status === true) {
        setOrderStatus(response.msg?.status);
        if (selectedAddressId) {
          setOrderAddressId(selectedAddressId);
        }
        showNotification('Order saved successfully!', 'success');
        return true;
      } else {
        throw new Error(response?.msg?.error || response?.body?.error || 'Failed to save order');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to save order', 'error');
      return false;
    } finally {
      setSavingOrder(false);
    }
  }, [orderId, currentRole, orderDeliveryCharge, handlingPackagingCharge, platformCharge, selectedAddressId]);

  // Submit order
  const submitOrder = useCallback(async (cart, batchUpdateCartItems, showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Order not created. Please select a service provider first.', 'error');
      return;
    }
    
    if (cart.filter(item => !item.outOfStock).length === 0) {
      showNotification('Order must have at least one non-out-of-stock item', 'error');
      return;
    }
    
    setSubmittingOrder(true);
    try {
      await batchUpdateCartItems();
      
      // This save is why `submit` itself needs no address handling: the pick is already
      // persisted (and ownership-checked) by the time the order is submitted.
      await saveManualServiceOrder(currentRole, orderId, {
        delivery_charges: orderDeliveryCharge !== null && orderDeliveryCharge !== undefined ? orderDeliveryCharge : 0,
        handling_packaging_charge: handlingPackagingCharge !== null && handlingPackagingCharge !== undefined ? handlingPackagingCharge : 0,
        platform_charge: platformCharge !== null && platformCharge !== undefined ? platformCharge : 0,
        taxes: 0,
        partner_charges: 0,
        ...(selectedAddressId ? { address_id: selectedAddressId } : {})
      });
      
      const response = await submitManualServiceOrder(currentRole, orderId);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        setOrderStatus(response.msg?.status);
        if (selectedAddressId) {
          setOrderAddressId(selectedAddressId);
        }
        showNotification('Order submitted successfully!', 'success');
        return true;
      } else {
        throw new Error(response?.msg?.error || response?.body?.error || 'Failed to submit order');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to submit order', 'error');
      return false;
    } finally {
      setSubmittingOrder(false);
    }
  }, [orderId, currentRole, orderDeliveryCharge, handlingPackagingCharge, platformCharge, selectedAddressId]);

  // Update order note
  const updateNote = useCallback(async (showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Order not found', 'error');
      return;
    }
    
    if (!noteText.trim()) {
      showNotification('Please enter a note', 'error');
      return;
    }
    
    setUpdatingNote(true);
    try {
      const noteToSend = noteText.trim();
      const response = await updateOrderNote(currentRole, orderId, noteToSend);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        if (response.msg && response.msg.order_note) {
          setNoteText(response.msg.order_note);
        }
        setSavedNote(response.msg?.order_note || noteToSend);
        showNotification('Order note updated successfully', 'success');
        return true;
      } else {
        const errorMsg = response?.msg?.error || response?.msg?.message || response?.body?.error || 'Failed to update note';
        throw new Error(errorMsg);
      }
    } catch (error) {
      showNotification(error.message || 'Failed to update note', 'error');
      return false;
    } finally {
      setUpdatingNote(false);
    }
  }, [orderId, currentRole, noteText]);

  // Cancel order
  const cancelOrderRequest = useCallback(async (cancellationReason, showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Order not found', 'error');
      return;
    }
    
    if (!cancellationReason) {
      showNotification('Please select a cancellation reason', 'error');
      return;
    }
    
    setCancellingOrder(true);
    try {
      const response = await cancelOrder(currentRole, orderId, cancellationReason);
      
      if (response && response.metadata && response.metadata.api_status === true) {
        showNotification('Order cancelled successfully', 'success');
        setOrderStatus('cancelled');
        return true;
      } else {
        throw new Error(response?.msg?.error || response?.body?.error || 'Failed to cancel order');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to cancel order', 'error');
      return false;
    } finally {
      setCancellingOrder(false);
    }
  }, [orderId, currentRole]);

  // Mark all items out of stock
  const markAllOutOfStock = useCallback(async (refreshCart, showNotification) => {
    if (!orderId || !currentRole) {
      showNotification('Order not created or cart is empty', 'error');
      return;
    }
    
    if (orderStatus && ['cancelled', 'delivered', 'ordered', 'out_of_stock'].includes(orderStatus)) {
      showNotification('Cannot mark items as out of stock for this order status', 'error');
      return;
    }
    
    setMarkingAllOutOfStock(true);
    try {
      const response = await markAllItemsOutOfStock(
        currentRole,
        orderId,
        noteText || null,
        orderDeliveryCharge !== null && orderDeliveryCharge !== undefined ? orderDeliveryCharge : null,
        0
      );
      
      if (response && response.metadata && response.metadata.api_status === true) {
        setOrderStatus('out_of_stock');
        showNotification('All items marked as out of stock. Order status updated.', 'success');
        await refreshCart();
        return true;
      } else {
        throw new Error(response?.msg?.error || response?.msg?.message || 'Failed to mark all items as out of stock');
      }
    } catch (error) {
      showNotification(error.message || 'Failed to mark all items as out of stock', 'error');
      return false;
    } finally {
      setMarkingAllOutOfStock(false);
    }
  }, [orderId, currentRole, orderStatus, noteText, orderDeliveryCharge]);

  // Update delivery charge
  const updateDeliveryCharge = useCallback((newCharge) => {
    const parsedCharge = newCharge === '' || newCharge === null || newCharge === undefined ? NaN : parseFloat(newCharge);
    const chargeValue = isNaN(parsedCharge) ? null : parsedCharge;
    setOrderDeliveryCharge(chargeValue);
    if (chargeValue !== null && chargeValue < 0) {
      setOrderDeliveryChargeError('Delivery Charge must be >= 0');
    } else {
      setOrderDeliveryChargeError(null);
    }
  }, []);

  // Update handling & packaging charge
  const updateHandlingPackagingCharge = useCallback((newCharge) => {
    const chargeValue = newCharge === '' || newCharge === null ? null : (parseFloat(newCharge) || null);
    setHandlingPackagingCharge(chargeValue);
    setHandlingPackagingChargeError(chargeValue !== null && chargeValue < 0 ? 'Handling & Packaging Fee must be >= 0' : null);
  }, []);

  // Update platform charge
  const updatePlatformCharge = useCallback((newCharge) => {
    const chargeValue = newCharge === '' || newCharge === null ? null : (parseFloat(newCharge) || null);
    setPlatformCharge(chargeValue);
    setPlatformChargeError(chargeValue !== null && chargeValue < 0 ? 'Platform Fee must be >= 0' : null);
  }, []);

  return {
    orderId,
    orderStatus,
    orderDeliveryCharge,
    orderDeliveryChargeError,
    handlingPackagingCharge,
    handlingPackagingChargeError,
    platformCharge,
    platformChargeError,
    noteText,
    savedNote,
    vendorId,
    serviceProvider,
    orderAddressId,
    selectedAddressId,
    setSelectedAddressId,
    isPaymentLocked,
    isPaid,
    prescriptions,
    savingOrder,
    submittingOrder,
    markingAllOutOfStock,
    updatingNote,
    cancellingOrder,
    setOrderId,
    setOrderStatus,
    setNoteText,
    setSavedNote,
    createOrder,
    loadPrefilledCart,
    saveOrder,
    submitOrder,
    updateNote,
    cancelOrderRequest,
    markAllOutOfStock,
    updateDeliveryCharge,
    updateHandlingPackagingCharge,
    updatePlatformCharge,
    orderLoadedRef,
    prefilledCartLoadedRef
  };
};

